'use client';

import { useEffect, useRef } from 'react';
import type { CSSProperties } from 'react';

import './section-eyebrow.css';

/**
 * The signature "corner-square" eyebrow: a bordered, rounded label box with two
 * small filled squares at the top-left and bottom-right corners. Shared by
 * /gradle (static) and the Develocity homepage (hero + section headers, with an
 * entrance animation).
 *
 * Defaults render the /gradle dark chip, so `<SectionEyebrow label={x} />` needs
 * zero extra config. Opt into the homepage looks with `size` + `animate`; every
 * per-instance value is a `--se-*` CSS custom property (see section-eyebrow.css)
 * a call site can override for pixel-exact colors (e.g. `--se-square: #fff`).
 *
 * Typography is intentionally NOT baked beyond the /gradle default — the three
 * live looks differ (sans uppercase sm / sans mixed-case 16px / mono 18px), so
 * callers set font on the root via `className` and it cascades to the label.
 *
 * Long-term this is a candidate to move into kraidle as `SectionEyebrow` once
 * light mode returns to the design system (kraidle is dark-only today); keep it
 * token-driven so that lift is mechanical.
 */
interface SectionEyebrowProps {
  label: string;
  /** Color palette. 'light' is for light-background sections (/gradle trust). */
  tone?: 'dark' | 'light';
  /** Geometry preset: 'lg' = /gradle chip (12px corners); 'md'/'sm' = homepage hero/section (9px). */
  size?: 'sm' | 'md' | 'lg';
  /** Entrance animation. Omit for a static render (the /gradle default). */
  animate?: 'load' | 'in-view';
  /** Caller typography (font size/weight/spacing/transform/family), cascades to the label. */
  className?: string;
  style?: CSSProperties;
}

export function SectionEyebrow({ label, tone = 'dark', size = 'lg', animate, className, style }: SectionEyebrowProps) {
  const rootRef = useRef<HTMLSpanElement>(null);

  // `in-view`: arm the CSS animation, then let it play once the chip scrolls in.
  // Mirrors the homepage's former per-pill observer (threshold 0.8, fire once).
  useEffect(() => {
    if (animate !== 'in-view') return;
    const el = rootRef.current;
    if (!el) return;
    const io = new IntersectionObserver(
      entries => {
        entries.forEach(e => {
          if (e.isIntersecting) {
            e.target.classList.add('in-view');
            io.unobserve(e.target);
          }
        });
      },
      { threshold: 0.8 },
    );
    io.observe(el);
    return () => io.disconnect();
  }, [animate]);

  return (
    <span
      ref={rootRef}
      className={className ? `section-eyebrow ${className}` : 'section-eyebrow'}
      data-tone={tone}
      data-size={size}
      data-animate={animate}
      style={style}
    >
      <span className="section-eyebrow__box">{label}</span>
    </span>
  );
}
